import React, { useEffect, useState } from 'react';
import { MapCameraChangedEvent, MapCameraProps } from '@vis.gl/react-google-maps';
import isEqual from 'lodash-es/isEqual';

import ErrorBoundary from '@rover/components/src/errorHandling/ErrorBoundary';
import { Box } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import Marker from '@rover/react-lib/src/components/GeoMap/Marker/Marker';
import { DEFAULT_MAP_ZOOM, Geometry } from '@rover/react-lib/src/components/GeoMap/types';
import type { RadiusUnit } from '@rover/react-lib/src/pages/provider-profile/types/hub/Radius';
import type { LatLng } from '@rover/types/src/LatLng';

import GeoMap, { GeoMapCircle, GeoMapPolygon } from '../../../../../../components/GeoMap';
import GeoMapErrorMessage from '../../../../../../components/GeoMap/GeoMapErrorMessage';

type Props = {
  center?: LatLng;
  onChange: (coordinates: LatLng) => void;
  radius: number;
  unit: RadiusUnit;
  geometry: object | null | undefined;
  readonly?: boolean;
  height?: React.ComponentProps<typeof Box>['height'];
  mb?: React.ComponentProps<typeof Box>['mb'];
};

const formatCoordinate = (coordinate: string | number): number => parseFloat(coordinate.toString());

const parseCenter = (center?: LatLng | null): LatLng<number> | undefined => {
  if (!center) return undefined;
  return {
    lat: formatCoordinate(center.lat),
    lng: formatCoordinate(center.lng),
  };
};

const defaultCenter = { lat: 0, lng: 0 };

const AreaMap = React.memo(
  ({
    center,
    onChange,
    radius,
    unit,
    geometry,
    readonly = false,
    height = '400px',
    mb = '6x',
  }: Props): JSX.Element => {
    const disableDefaultUI = readonly;
    const gestureHandling = readonly ? 'none' : 'cooperative';
    const zoomControl = !readonly;
    const showCenterMarker = !readonly;
    const clickable = !readonly;
    const [controlledMapProps, setControlledMapProps] = useState<MapCameraProps>({
      center: parseCenter(center) || defaultCenter,
      zoom: DEFAULT_MAP_ZOOM,
    });
    useEffect(() => {
      if (!center) return;
      setControlledMapProps((prev) => {
        return {
          zoom: DEFAULT_MAP_ZOOM,
          center: parseCenter(center) || defaultCenter,
          ...{ prev },
        } as MapCameraProps;
      });
    }, [center]);

    const { colors } = useTheme();

    const handleOnCenterChanged = (mapChangeEvent: MapCameraChangedEvent): void => {
      const newCenter = mapChangeEvent.detail.center;
      if (isEqual(newCenter, center)) {
        return;
      }
      onChange(newCenter);
    };
    return (
      <ErrorBoundary
        location="ServiceEditingMap"
        messageComponent={<GeoMapErrorMessage minHeight={height} mb={mb} />}
      >
        <Box height={height} mb={mb} position="relative" data-testid="area-map">
          <GeoMap
            center={controlledMapProps.center}
            disableDoubleClickZoom
            minZoom={6}
            onCenterChanged={handleOnCenterChanged}
            zoom={controlledMapProps.zoom}
            disableDefaultUI={disableDefaultUI}
            gestureHandling={gestureHandling}
            zoomControl={zoomControl}
          >
            {geometry ? (
              <>
                <GeoMapPolygon geometry={geometry as Geometry} clickable={clickable} />
                {center && showCenterMarker && (
                  <Marker
                    position={{
                      lat: typeof center.lat === 'string' ? parseFloat(center.lat) : center.lat,
                      lng: typeof center.lng === 'string' ? parseFloat(center.lng) : center.lng,
                    }}
                    color={colors.green['600']}
                  />
                )}
              </>
            ) : (
              <GeoMapCircle center={center} radius={radius} unit={unit} />
            )}
          </GeoMap>
        </Box>
      </ErrorBoundary>
    );
  },
  isEqual
);

export default AreaMap;
